/**
 * iCalendar file for one booking. UID is stable per booking and SEQUENCE is
 * the booking version, so re-downloading after a reschedule updates the same
 * event in calendars that honour it. The file is a snapshot: it does not
 * update by itself — the UI says so.
 */
const pad = (n: number) => String(n).padStart(2, '0');
const utc = (iso: string) => {
  const d = new Date(iso);
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
};
const esc = (s: string) => s.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/[,;]/g, (m) => `\\${m}`);

/** RFC 5545 line folding at 75 octets. */
function fold(line: string): string {
  const bytes = new TextEncoder().encode(line);
  if (bytes.length <= 75) return line;
  const out: string[] = [];
  let current = '';
  for (const ch of line) {
    if (new TextEncoder().encode(current + ch).length > (out.length ? 74 : 75)) {
      out.push(current);
      current = ch;
    } else current += ch;
  }
  out.push(current);
  return out.join('\r\n ');
}

export interface IcsInput {
  bookingId: string;
  version: number;
  code: string;
  studioName: string;
  serviceName: string;
  startsAt: string;
  endsAt: string;
  address: string;
  phone: string;
  url: string;
  cancelled: boolean;
  reminderHours: number;
}

export function buildIcs(b: IcsInput): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//studio-booking//RU',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${b.bookingId}@studio-booking`,
    `SEQUENCE:${b.version}`,
    `DTSTAMP:${utc(new Date().toISOString())}`,
    `DTSTART:${utc(b.startsAt)}`,
    `DTEND:${utc(b.endsAt)}`,
    `SUMMARY:${esc(`${b.serviceName} — ${b.studioName}`)}`,
    `LOCATION:${esc(b.address)}`,
    `DESCRIPTION:${esc(`Код записи ${b.code}. Телефон студии ${b.phone}. Запись: ${b.url}`)}`,
    `URL:${b.url}`,
    `STATUS:${b.cancelled ? 'CANCELLED' : 'CONFIRMED'}`,
    ...(b.cancelled
      ? []
      : ['BEGIN:VALARM', 'ACTION:DISPLAY', `DESCRIPTION:${esc(`Завтра: ${b.serviceName}`)}`, `TRIGGER:-PT${b.reminderHours}H`, 'END:VALARM']),
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return lines.map(fold).join('\r\n') + '\r\n';
}

export function downloadIcs(input: IcsInput) {
  const blob = new Blob([buildIcs(input)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `zapis-${input.code}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
